import { useState, useEffect } from 'react';
import config from '../config';
import {
  getAudios,
  createAudio,
  updateAudio,
  deleteAudio,
} from '../services/audios';
import { Audio, AudioCreateOrUpdate, MODES } from '../types/audio';
import { useParams } from 'react-router-dom';
import { useConfirm } from '../contexts/ConfirmDialogContext';

interface AudiosListProps {
  projectName: string;
  section: string;
  chapter: number;
  number: number;
}
const AUDIO_BASE_URL = config.apiUrl; // backend URL

export default function AudiosList({ projectName, section, chapter, number }: AudiosListProps) {
  console.log('AudiosList props:', { projectName, section, chapter, number });

  const [audios, setAudios] = useState<Audio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newAudio, setNewAudio] = useState<AudioCreateOrUpdate>({
    file_path: null,
    mode: MODES.CHANT,
  });
  const [editingAudio, setEditingAudio] = useState<AudioCreateOrUpdate | null>(null);
  const confirm = useConfirm();

  useEffect(() => {
    console.log('useEffect triggered, calling fetchAudios');
    fetchAudios();
  }, [projectName, section, chapter, number]);

  const fetchAudios = async () => {
    try {
      setLoading(true);
      console.log('Fetching audios with:', { projectName, section, chapter, number });
      const data = await getAudios(projectName, section, chapter, number);
      console.log('Fetched audios:', data);
      setAudios(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching audios:', err);
      if (err instanceof Error && err.message.includes('404')) {
        setAudios([]);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch audios');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const created = await createAudio(projectName, section, chapter, number, newAudio);
  
      // Wrap the file_path into the expected structure
      const adapted: Audio = {
        mode: newAudio.mode,
        file_path: { file_path: created.file_path },
      };
  
      setAudios([...audios, adapted]);
      setNewAudio({ file_path: null, mode: MODES.CHANT });
    } catch (err) {
      console.error('Error creating audio:', err);
      setError(err instanceof Error ? err.message : 'Failed to create audio');
    }
  };
  

  const handleUpdate = async () => {
    if (!editingAudio) return;
    try {
      console.log('Updating audio with:', { projectName, section, chapter, number, editingAudio });
      const updated = await updateAudio(projectName, section, chapter, number, editingAudio);
      console.log('Updated audio:', updated);
      setAudios(audios.map(i =>
        i.mode === updated.mode ? updated : i
      ));
      setEditingAudio(null);
    } catch (err) {
      console.error('Error updating audio:', err);
      setError(err instanceof Error ? err.message : 'Failed to update audio');
    }
  };

  const handleDelete = async (mode: string) => {
        const ok = await confirm({
            title: 'Confirm Delete',
            message: `Are you sure you want to delete ...?`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
        });

        if (!ok) return;

    try {
      console.log('Deleting audio with:', { projectName, section, chapter, number, mode });
      await deleteAudio(projectName, section, chapter, number, mode);
      setAudios(audios.filter(i => i.mode !== mode));
    } catch (err) {
      console.error('Error deleting audio:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete audio');
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Audio for Sutra {section}/{chapter}/{number} in {projectName}</h2>

      {/* Add New Audio */}
      <div className="mb-6 p-4 border rounded">
        <h3 className="text-xl font-semibold mb-2">Add New Audio</h3>
        <div className="space-y-2">
          <input
            type="file"
            accept="audio/*"
            onChange={(e) =>
              setNewAudio({ ...newAudio, file_path: e.target.files?.[0] || null })
            }
            className="w-full"
          />
          <select
            className="p-2 border rounded w-full"
            value={newAudio.mode}
            onChange={(e) => setNewAudio({ ...newAudio, mode: e.target.value })}
          >
            {Object.entries(MODES).map(([key, value]) => (
              <option key={value} value={value}>{key}</option>
            ))}
          </select>
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded"
            onClick={handleCreate}
          >
            Add Audio
          </button>
        </div>
      </div>

      {/* Error and Loading States */}
      {loading && <div>Loading...</div>}
      {error && <div className="text-red-500">Error: {error}</div>}

      {/* Audio List */}
      <div className="space-y-4">
        {!loading && !error && audios.length === 0 ? (
          <div>No audios found for this sutra. Add a new audio above.</div>
        ) : (
          audios.map((audio) => (
            <div key={audio.mode} className="border p-4 rounded space-y-2">
              <p className="text-lg font-semibold">File: {audio.file_path.file_path}</p>
              <p className="text-sm text-gray-600">Mode: {audio.mode}</p>

              <audio
                controls
                // src={`/${audio.file_path.file_path}`}
                src={`${AUDIO_BASE_URL}/${audio.file_path.file_path}`}
                className="w-full"
              >
                Your browser does not support the audio element.
              </audio>

              {editingAudio?.mode === audio.mode ? (
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setEditingAudio(prev =>
                          prev ? { ...prev, file_path: file } : null
                        );
                      }
                    }}
                    className="w-full"
                  />
                  <div className="flex space-x-2">
                    <button
                      className="bg-green-500 text-white px-4 py-2 rounded"
                      onClick={handleUpdate}
                    >
                      Save
                    </button>
                    <button
                      className="bg-gray-500 text-white px-4 py-2 rounded"
                      onClick={() => setEditingAudio(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex space-x-2">
                  <button
                    className="bg-yellow-500 text-white px-4 py-2 rounded"
                    onClick={() =>
                      setEditingAudio({
                        file_path: null, // user will re-select file to upload
                        mode: audio.mode,
                      })
                    }
                    
                  >
                    Edit
                  </button>
                  <button
                    className="bg-red-500 text-white px-4 py-2 rounded"
                    onClick={() => handleDelete(audio.mode)}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
